import { useEffect, useState } from 'preact/hooks';
import { Credit, Placeholder } from './chrome';
import { watchClass, watchShow } from './firebase';
import { formatClassDate, STATUS_LABEL } from './format';
import { sliceRows, type WidgetParams } from './params';
import { ResultsTable } from './results-table';
import type { PublicResultsClassFS } from './types';

/** One class, named by the embed or resolved from the show. The original widget. */
export function SingleClass({ params }: { params: WidgetParams }) {
  const [selectedClassId, setSelectedClassId] = useState<string | null>(params.classId);
  const [classDoc, setClassDoc] = useState<PublicResultsClassFS | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // The show index is read only to work out which class a show-only embed should
  // open on. An embed that names its class needs neither the listener nor the
  // document read, so it skips this entirely.
  useEffect(() => {
    if (!params.showId || params.classId) return undefined;
    return watchShow(
      params.showId,
      (data) => {
        // Open on the class most likely being watched: the one in progress, else
        // the first. `classes` is optional-chained as well as `data`: a show
        // document written without the field would otherwise throw inside the
        // snapshot handler, which fails exactly as silently as resolving nothing.
        const live = data?.classes?.find((cls) => cls.status === 'in_progress');
        const resolved = live?.classId ?? data?.classes?.[0]?.classId ?? null;

        // When nothing resolves — no show document, or one with no classes — no
        // watchClass subscription is ever created, so this is the only place that
        // can end the loading state. Without it the widget sits on "Loading
        // results…" forever instead of falling through to the empty state.
        if (!resolved) setLoading(false);

        // Resolved once: a later change of live class must not yank the table out
        // from under someone mid-read.
        setSelectedClassId((current) => current ?? resolved);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [params.showId, params.classId]);

  useEffect(() => {
    // Nothing to subscribe to yet. The show-index effect above owns the loading
    // state until it resolves a class id.
    if (!selectedClassId) return undefined;
    setLoading(true);
    setError(null);
    return watchClass(
      selectedClassId,
      (data) => {
        setClassDoc(data);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [selectedClassId]);

  if (!params.showId && !params.classId) {
    return <Placeholder tone="error" message="This embed is missing a show or class parameter." />;
  }
  if (error) {
    return <Placeholder tone="error" message={`Results are unavailable right now. (${error})`} />;
  }
  if (loading) {
    return <Placeholder tone="loading" message="Loading results…" />;
  }
  if (!classDoc) {
    return <Placeholder message="Results for this class have not been published yet." />;
  }

  // The projection document always carries every entry, so changing how many are
  // shown is a client-side slice — no re-sync, and `limit=all` costs no extra
  // reads. Rows arrive already ordered by placing, so the front of the list is
  // the podium.
  const visibleRows = sliceRows(classDoc.rows, params.limit);
  const isTruncated = classDoc.rowCount > visibleRows.length;

  return (
    <div class="widget">
      <p class="eyebrow">Class Results</p>

      <header class="header">
        {classDoc.classNumber && <p class="class-number">Class {classDoc.classNumber}</p>}
        <h1 class="title">{classDoc.className}</h1>
        <div class="meta-row">
          <span class={`badge badge--${classDoc.status}`}>{STATUS_LABEL[classDoc.status]}</span>
          <span class="meta">
            {[classDoc.showName, formatClassDate(classDoc.classDate, classDoc.timezone), classDoc.heightLabel]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      </header>

      {classDoc.rows.length === 0 ? (
        <Placeholder message="No entries have been published for this class yet." />
      ) : (
        <ResultsTable rows={visibleRows} status={classDoc.status} />
      )}

      <footer class="footer">
        <span>
          {isTruncated ? `Top ${visibleRows.length} of ${classDoc.rowCount}` : classDoc.rowCount}{' '}
          entries
        </span>
        <Credit />
      </footer>
    </div>
  );
}
